// The one popup shown when something is blocked by the RFP trial/subscription
// (see events/planLimitEvents.js). Mounted once in App.js. For the project limit it also shows
// how the limit comes from the seats, and lets the owner add a seat on the spot or book a call
// about Enterprise.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";

import Dialog from "../pages/Account/billing/Dialog";
import ManageSeatsDialog from "../pages/Account/billing/ManageSeatsDialog";
import { onPlanLimit } from "../events/planLimitEvents";
import { PROJECTS_PER_SEAT, SALES_URL, SEAT_MAX, fetchRfpSubscription, formatMoney } from "../services/rfpBillingApi";
import { currentWorkspacePermission } from "../utils/workspace";

// Above every other modal in the app, since this opens on top of whatever was refused.
const Z_CLASS = "z-[100000]";
const PRIMARY =
  "inline-flex items-center rounded-[9px] bg-[#A04A97] px-5 py-2.5 text-[14px] font-semibold text-white shadow-[0_2px_8px_rgba(160,74,151,0.35)] transition-colors hover:bg-[#8F4188]";
const SECONDARY = "rounded-[9px] px-3.5 py-2 text-[13.5px] font-medium text-[#52525B] transition-colors hover:bg-[#F4F4F5]";
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// How many of the allowance are used, on the same orange-to-plum bar as the plan card.
const Meter = ({ label, used, limit }) => (
  <div>
    <div className="flex items-baseline justify-between text-[12.5px] text-[#52525B]">
      <span>{label}</span>
      <span className="font-medium text-[#1A1A1A] [font-variant-numeric:tabular-nums]">
        {used} of {limit}
      </span>
    </div>
    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#ECE9EE]">
      <div
        className="h-full rounded-full bg-gradient-to-r from-[#f3904f] to-[#C95EBE]"
        style={{ width: `${Math.min(100, (used / limit) * 100)}%` }}
      />
    </div>
  </div>
);

const BOX = "rounded-[14px] border border-[#ECE9EE] bg-[#FAF9FB]";

// What the popup says and offers for the project limit, from the workspace's subscription.
const projectLimitView = (sub) => {
  const { used, limit } = sub.usage.projects;
  const isOwner = currentWorkspacePermission() === "owner";

  const meter = (label) => (
    <div className={`${BOX} px-4 py-3.5`}>
      <Meter label={label} used={used} limit={limit} />
    </div>
  );

  if (sub.state === "trialing") {
    return {
      title: `Your trial includes ${plural(limit, "project")}`,
      text: `Deleted projects count too. On a paid plan, each seat adds ${PROJECTS_PER_SEAT} projects for ${formatMoney(sub.unit_amount, sub.currency)} a month.`,
      body: meter("Created in your trial"),
      primary: isOwner ? "choose" : "billing",
      note: isOwner ? null : "Ask the workspace owner to choose a plan.",
    };
  }

  const seats = sub.seat_quantity;
  const perSeat = Math.round(limit / seats);
  const atMax = seats >= SEAT_MAX;
  const canAdd = !atMax && ["active", "renewal_due"].includes(sub.state) && !sub.cancel_at_period_end;
  return {
    title: `You've used all ${limit} projects`,
    text: `${
      seats === 1 ? `Your 1 seat includes ${perSeat} projects.` : `Your ${seats} seats include ${perSeat} projects each, ${limit} in total.`
    } Deleted projects still count.`,
    body: (
      <>
        {meter("Created so far")}
        {canAdd && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-[12px] border border-[#E9D2E6] bg-[#FBF5FA] px-4 py-3">
            <span className="text-[13.5px] text-[#1A1A1A]">
              <b className="font-semibold">+1 seat</b> → {limit + perSeat} projects
            </span>
            <span className="text-[13px] font-medium text-[#893F82]">
              +{formatMoney(sub.unit_amount, sub.currency)} / month
            </span>
          </div>
        )}
      </>
    ),
    primary: atMax ? "sales" : canAdd && isOwner ? "seats" : "billing",
    note: atMax
      ? `${SEAT_MAX} seats is the most a workspace can buy here. For more projects, we can set you up on an Enterprise plan.`
      : canAdd && !isOwner
      ? "Only the workspace owner can add seats."
      : null,
  };
};

const PlanLimitHost = () => {
  const [limit, setLimit] = useState(null); // { message, subscription? }
  const [seatsFor, setSeatsFor] = useState(null); // subscription whose seats are being changed
  const navigate = useNavigate();

  useEffect(
    () =>
      onPlanLimit((message, kind) => {
        if (kind !== "projects") {
          setLimit({ message });
          return;
        }
        // The numbers come from the workspace's subscription; if that fails, it's just the message.
        fetchRfpSubscription().then(
          (subscription) => setLimit({ message, subscription }),
          () => setLimit({ message })
        );
      }),
    []
  );

  const close = () => setLimit(null);
  const toBilling = () => {
    close();
    navigate("/account/billing");
  };

  const sub = limit?.subscription;
  // The project numbers only explain a refusal while the plan is active; a locked or never-started
  // plan gets the server's own message ("…plan isn't active…") instead of "0 of 10 used".
  const view = sub?.can_use && sub?.usage?.projects?.limit ? projectLimitView(sub) : null;

  const primaryButton = !view ? (
    <button type="button" onClick={toBilling} className={PRIMARY}>
      View plans
    </button>
  ) : view.primary === "seats" ? (
    <button
      type="button"
      onClick={() => {
        setSeatsFor(sub);
        close();
      }}
      className={PRIMARY}
    >
      Add a seat
    </button>
  ) : view.primary === "sales" ? (
    <a href={SALES_URL} target="_blank" rel="noopener noreferrer" onClick={close} className={PRIMARY}>
      Book a call
    </a>
  ) : (
    <button type="button" onClick={toBilling} className={PRIMARY}>
      {view.primary === "choose" ? "Choose a plan" : "View billing"}
    </button>
  );

  return (
    <>
      <Dialog
        open={Boolean(limit)}
        onClose={close}
        title={view ? view.title : "Upgrade to continue"}
        maxWidth={view ? 460 : 440}
        zClass={Z_CLASS}
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F6E9F4] text-[#A04A97]">
          <Lock size={20} />
        </div>
        <h2 className="mt-4 text-[19px] font-semibold tracking-[-0.01em] text-[#1A1A1A]">
          {view ? view.title : "Upgrade to continue"}
        </h2>
        <p className="mt-2 text-[14px] leading-[1.55] text-[#52525B]">{view ? view.text : limit?.message}</p>

        {view && <div className="mt-4">{view.body}</div>}
        {view?.note && <p className="mt-3 text-[13px] leading-[1.5] text-[#52525B]">{view.note}</p>}
        {view && view.primary !== "sales" && (
          <p className="mt-3 text-[13px] text-[#8A8A90]">
            Need more than {SEAT_MAX} seats?{" "}
            <a href={SALES_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-[#893F82] hover:underline">
              Talk to sales
            </a>
          </p>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={close} className={SECONDARY}>
            Not now
          </button>
          {primaryButton}
        </div>
      </Dialog>

      {seatsFor && (
        <ManageSeatsDialog
          open
          onClose={() => setSeatsFor(null)}
          currentSeats={seatsFor.seat_quantity}
          initialSeats={Math.min(SEAT_MAX, seatsFor.seat_quantity + 1)}
          seatsUsed={seatsFor.seats?.used ?? 1}
          unitAmount={seatsFor.unit_amount}
          periodStart={seatsFor.current_period_start}
          periodEnd={seatsFor.current_period_end}
          zClass={Z_CLASS}
        />
      )}
    </>
  );
};

export default PlanLimitHost;
